import { fetchImageAsBlobUrl } from "./images";

type CacheEntry = {
  blobUrl: string | null;
  refCount: number;
  promise: Promise<string | null> | null;
};

const cache = new Map<string, CacheEntry>();

function getOrCreateEntry(proxiedUrl: string): CacheEntry {
  let entry = cache.get(proxiedUrl);
  if (!entry) {
    entry = { blobUrl: null, refCount: 0, promise: null };
    cache.set(proxiedUrl, entry);
  }
  return entry;
}

/** Fetch into cache without holding a consumer reference — for startup prefetch. */
export function warmSharedImageBlobUrl(proxiedUrl: string): void {
  const entry = getOrCreateEntry(proxiedUrl);
  if (entry.blobUrl || entry.promise) return;

  entry.promise = fetchImageAsBlobUrl(proxiedUrl).then((blobUrl) => {
    entry.blobUrl = blobUrl;
    entry.promise = null;
    return blobUrl;
  });
}

export async function acquireSharedImageBlobUrl(
  proxiedUrl: string,
): Promise<string | null> {
  const entry = getOrCreateEntry(proxiedUrl);
  entry.refCount += 1;

  if (entry.blobUrl) {
    return entry.blobUrl;
  }

  if (!entry.promise) {
    entry.promise = fetchImageAsBlobUrl(proxiedUrl).then((blobUrl) => {
      entry.blobUrl = blobUrl;
      entry.promise = null;
      return blobUrl;
    });
  }

  return entry.promise;
}

export function releaseSharedImageBlobUrl(proxiedUrl: string): void {
  const entry = cache.get(proxiedUrl);
  if (!entry) return;

  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount > 0) return;

  if (entry.blobUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(entry.blobUrl);
  }
  cache.delete(proxiedUrl);
}

/** Test-only reset. */
export function resetSharedImageBlobCacheForTests(): void {
  for (const [proxiedUrl, entry] of cache.entries()) {
    if (entry.blobUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(entry.blobUrl);
    }
    cache.delete(proxiedUrl);
  }
}
